import type { FieldWidth, ILocalizedText } from '@platform/core/types';
import type { IProtoForm } from './IProtoForm';
import type { IProtoGroup } from './IProtoGroup';

/** Значення вибору групи «Приховано» (прототип v4 '__hide'). */
export const HIDE = '__hide';

const swap = <T>(list: readonly T[], i: number, j: number): T[] => {
  if (i < 0 || j < 0 || i >= list.length || j >= list.length) return [...list];
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
};
const withGroup = (form: IProtoForm, id: string, fn: (g: IProtoGroup) => IProtoGroup): IProtoForm => ({ ...form, groups: form.groups.map((g) => (g.id === id ? fn(g) : g)) });

/**
 * Групи й поля редактора форми (прототип v4 feGroups / feHidden): назва групи (нова назва — новий ідентифікатор `renamedId`: стандартна
 * група з перекладом за ідентифікатором стає групою користувача), «Увесь рядок», порядок груп і полів, переміщення поля в групу чи до
 * прихованих, нова група, вилучення групи (її поля стають прихованими), явна ширина поля або «Авто». Чисті функції.
 */
export const groupEdits = {
  rename: (form: IProtoForm, id: string, name: string, renamedId: string): IProtoForm => withGroup(form, id, (g) => ({ ...g, id: renamedId, title: { en: name, uk: name } })),
  full: (form: IProtoForm, id: string): IProtoForm => withGroup(form, id, (g) => ({ ...g, full: !g.full })),
  moveGroup: (form: IProtoForm, id: string, step: -1 | 1): IProtoForm => {
    const i = form.groups.findIndex((g) => g.id === id);
    return { ...form, groups: swap(form.groups, i, i + step) };
  },
  moveField: (form: IProtoForm, id: string, key: string, step: -1 | 1): IProtoForm => withGroup(form, id, (g) => {
    const i = g.keys.indexOf(key);
    return { ...g, keys: swap(g.keys, i, i + step) };
  }),
  moveTo: (form: IProtoForm, key: string, target: string): IProtoForm => {
    const groups = form.groups.map((g) => ({ ...g, keys: g.keys.filter((k) => k !== key) }));
    const hidden = form.hidden.filter((k) => k !== key);
    if (target === HIDE) return { ...form, groups, hidden: [...hidden, key] };
    return { ...form, hidden, groups: groups.map((g) => (g.id === target ? { ...g, keys: [...g.keys, key] } : g)) };
  },
  add: (form: IProtoForm, id: string, title: ILocalizedText): IProtoForm => ({ ...form, groups: [...form.groups, { id, title, full: false, keys: [] }] }),
  remove: (form: IProtoForm, id: string): IProtoForm => {
    const gone = form.groups.find((g) => g.id === id);
    return { ...form, groups: form.groups.filter((g) => g.id !== id), hidden: [...form.hidden, ...(gone?.keys ?? [])] };
  },
  width: (form: IProtoForm, key: string, width: FieldWidth | null): IProtoForm => {
    const widths = { ...form.widths };
    if (width) widths[key] = width;
    else delete widths[key];
    return { ...form, widths };
  },
};
